import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Save, ExternalLink } from "lucide-react";

const EMPTY = {
  title: "", author: "", badge: "", partner: false, categories: [], cover_url: "",
  summary: "", why: "", price: "", isbn: "", published_at: "", biography: "",
  buttons: [{ label: "", url: "", kind: "primary" }], featured: false, order: 100, published: true,
};

export default function AdminBooks() {
  const [books, setBooks] = useState([]);
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/books"); setBooks(data.books); setCats(data.categories); }
    catch (e) { toast.error(apiErr(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingId(null); setForm({ ...EMPTY, buttons: [{ label: "", url: "", kind: "primary" }] }); };
  const openEdit = (b) => { setEditingId(b.book_id); setForm({ ...EMPTY, ...b, buttons: b.buttons?.length ? b.buttons : [{ label: "", url: "", kind: "primary" }] }); };

  const toggleCat = (key) => setForm((f) => ({ ...f, categories: f.categories.includes(key) ? f.categories.filter((c) => c !== key) : [...f.categories, key] }));
  const setBtn = (i, k, v) => setForm((f) => ({ ...f, buttons: f.buttons.map((b, idx) => idx === i ? { ...b, [k]: v } : b) }));
  const addBtn = () => setForm((f) => ({ ...f, buttons: [...f.buttons, { label: "", url: "", kind: "secondary" }] }));
  const rmBtn = (i) => setForm((f) => ({ ...f, buttons: f.buttons.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.title) return toast.error("Le titre est requis.");
    const payload = { ...form, order: Number(form.order) || 100, buttons: form.buttons.filter((b) => b.label && b.url) };
    try {
      if (editingId) await api.put(`/admin/books/${editingId}`, payload);
      else await api.post("/admin/books", payload);
      toast.success(editingId ? "Livre mis à jour." : "Livre ajouté.");
      setForm(null); setEditingId(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { if (!window.confirm("Supprimer ce livre ?")) return; try { await api.delete(`/admin/books/${id}`); load(); } catch (e) { toast.error(apiErr(e)); } };

  const inputCls = "w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 outline-none text-sm";
  const Field = ({ label, children }) => (<label className="block"><span className="text-white/60 text-xs mb-1 block">{label}</span>{children}</label>);

  if (form) {
    return (
      <div className="card-surface p-5 max-w-3xl" data-testid="books-admin-form">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg">{editingId ? "Modifier le livre" : "Nouveau livre"}</h3>
          <button onClick={() => setForm(null)} className="text-white/50 hover:text-white"><X size={18} /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <Field label="Titre"><input data-testid="books-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} /></Field>
          <Field label="Autrice / Auteur"><input data-testid="books-form-author" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className={inputCls} /></Field>
          <Field label="Badge (ex: Autrice partenaire BIZARRE AI)"><input value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} className={inputCls} /></Field>
          <Field label="URL de la couverture"><input data-testid="books-form-cover" value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} className={inputCls} placeholder="/books/mon-livre.webp ou https://..." /></Field>
        </div>

        <span className="text-white/60 text-xs mb-1 block">Catégories</span>
        <div className="flex flex-wrap gap-2 mb-3">
          {cats.map((c) => (
            <button key={c.key} type="button" data-testid={`books-form-cat-${c.key}`} onClick={() => toggleCat(c.key)}
              className={`text-sm rounded-full px-3 py-1.5 border ${form.categories.includes(c.key) ? "border-gold/50 bg-gold/10 text-gold" : "border-white/12 text-white/60"}`}>
              {c.emoji} {c.label}
            </button>
          ))}
        </div>

        <Field label="Résumé"><textarea rows={4} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className={`${inputCls} resize-none mb-3`} /></Field>
        <Field label="Pourquoi BIZARRE AI recommande ce livre"><textarea rows={3} value={form.why} onChange={(e) => setForm({ ...form, why: e.target.value })} className={`${inputCls} resize-none mb-3`} /></Field>
        <div className="grid sm:grid-cols-3 gap-3 mb-3">
          <Field label="Prix"><input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputCls} /></Field>
          <Field label="ISBN"><input value={form.isbn} onChange={(e) => setForm({ ...form, isbn: e.target.value })} className={inputCls} /></Field>
          <Field label="Publié le"><input value={form.published_at} onChange={(e) => setForm({ ...form, published_at: e.target.value })} className={inputCls} /></Field>
        </div>
        <Field label="Biographie"><textarea rows={4} value={form.biography} onChange={(e) => setForm({ ...form, biography: e.target.value })} className={`${inputCls} resize-none mb-3`} /></Field>

        <span className="text-white/60 text-xs mb-1 block">Boutons d'action</span>
        <div className="space-y-2 mb-3">
          {form.buttons.map((b, i) => (
            <div key={i} className="flex flex-wrap gap-2 items-center">
              <input value={b.label} onChange={(e) => setBtn(i, "label", e.target.value)} placeholder="Libellé" className={`${inputCls} flex-1 min-w-[140px]`} />
              <input value={b.url} onChange={(e) => setBtn(i, "url", e.target.value)} placeholder="https://..." className={`${inputCls} flex-1 min-w-[160px]`} />
              <select value={b.kind} onChange={(e) => setBtn(i, "kind", e.target.value)} className="bg-white/[0.06] border border-white/15 rounded-xl px-2 py-2 text-sm">
                <option value="primary">Principal</option><option value="secondary">Secondaire</option>
              </select>
              <button type="button" onClick={() => rmBtn(i)} className="text-white/40 hover:text-red-400"><X size={16} /></button>
            </div>
          ))}
          <button type="button" onClick={addBtn} className="text-gold text-sm inline-flex items-center gap-1"><Plus size={14} /> Ajouter un bouton</button>
        </div>

        <div className="flex flex-wrap gap-4 items-center mb-4">
          <label className="flex items-center gap-2 text-sm text-white/70"><input type="checkbox" checked={form.partner} onChange={(e) => setForm({ ...form, partner: e.target.checked })} /> Partenaire</label>
          <label className="flex items-center gap-2 text-sm text-white/70"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Mis en avant</label>
          <label className="flex items-center gap-2 text-sm text-white/70"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Publié</label>
          <Field label="Ordre"><input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className={`${inputCls} w-24`} /></Field>
        </div>
        <button data-testid="books-form-save" onClick={save} className="gold-gradient text-black rounded-full px-6 py-2.5 text-sm font-medium inline-flex items-center gap-2"><Save size={16} /> Enregistrer</button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-white/40 text-xs">{books.length} livre(s)</p>
        <button data-testid="books-admin-new" onClick={openCreate} className="gold-gradient text-black rounded-xl px-4 py-2 text-sm font-medium inline-flex items-center gap-1.5"><Plus size={16} /> Nouveau livre</button>
      </div>
      <div className="space-y-2">
        {books.map((b) => (
          <div key={b.book_id} data-testid={`books-admin-row-${b.book_id}`} className="card-surface p-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              {b.cover_url && <img src={b.cover_url} alt="" className="w-9 h-12 object-cover rounded-md shrink-0" />}
              <div className="min-w-0">
                <p className="text-sm truncate">{b.title} {!b.published && <span className="text-white/40 text-xs">(brouillon)</span>}</p>
                <p className="text-white/35 text-xs mt-0.5">{b.author} · {(b.categories || []).length} catégorie(s)</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => openEdit(b)} className="text-white/50 hover:text-gold"><Pencil size={16} /></button>
              <button onClick={() => del(b.book_id)} className="text-white/50 hover:text-red-400"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
        {books.length === 0 && <p className="text-white/30 text-sm">Aucun livre.</p>}
      </div>
    </div>
  );
}
